import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PencilSimple } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Md } from '@/components/Md';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PreConsultResponses from './helpers/PreConsultResponses';
import { useConfirmClear, useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtStamp } from '@/utils/dateFmt';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const ANAMNESE_BLOCKS = [
  { id: 'motivo', title: 'Motivo e objetivos', fields: [
    { key: 'motivo_consulta', label: 'Motivo da consulta', type: 'text' },
    { key: 'expectativas', label: 'Expectativas', type: 'text' },
    { key: 'objetivos_clinicos', label: 'Objetivos clínicos', type: 'text' },
    { key: 'motivo_outras', label: 'Outras informações', type: 'text' },
  ]},
  { id: 'estilo_vida', title: 'Estilo de vida', fields: [
    { key: 'funcao_intestinal', label: 'Função intestinal', type: 'choice', options: ['Regular', 'Obstipação', 'Diarreia', 'Alternado'] },
    { key: 'qualidade_sono', label: 'Qualidade do sono', type: 'scale' },
    { key: 'fumadora', label: 'Fumadora', type: 'boolean' },
    { key: 'consumo_alcool', label: 'Consumo de álcool', type: 'boolean' },
    { key: 'frequencia_alcool', label: 'Frequência do consumo', type: 'text' },
    { key: 'estado_civil', label: 'Estado civil', type: 'text' },
    { key: 'atividade_fisica', label: 'Atividade física', type: 'text' },
    { key: 'hora_levantar', label: 'Hora habitual de levantar', type: 'time' },
    { key: 'hora_deitar', label: 'Hora habitual de deitar', type: 'time' },
    { key: 'estilo_vida_outras', label: 'Outras informações', type: 'text' },
  ]},
  { id: 'historia_clinica', title: 'História clínica', fields: [
    { key: 'patologias', label: 'Patologias', type: 'text' },
    { key: 'medicacao', label: 'Medicação', type: 'text' },
    { key: 'antecedentes_pessoais', label: 'Antecedentes pessoais', type: 'text' },
    { key: 'antecedentes_familiares', label: 'Antecedentes familiares', type: 'text' },
    { key: 'historia_clinica_outras', label: 'Outras informações', type: 'text' },
  ]},
  { id: 'alimentacao', title: 'Alimentação', fields: [
    { key: 'tipo_dieta', label: 'Tipo de dieta', type: 'choice', options: ['Omnívora', 'Vegetariana', 'Vegana', 'Mediterrânica', 'Sem restrições', 'Outra'] },
    { key: 'alimentos_preferidos', label: 'Alimentos preferidos', type: 'text' },
    { key: 'alimentos_preteridos', label: 'Alimentos preteridos', type: 'text' },
    { key: 'alergias', label: 'Alergias', type: 'text' },
    { key: 'intolerancias', label: 'Intolerâncias alimentares', type: 'text' },
    { key: 'defices_nutricionais', label: 'Défices nutricionais', type: 'text' },
    { key: 'ingestao_agua', label: 'Ingestão de água (L/dia)', type: 'text' },
    { key: 'alimentacao_outras', label: 'Outras informações', type: 'text' },
  ]},
  { id: 'comportamento', title: 'Comportamento alimentar', fields: [
    { key: 'diario_alimentar_resumo', label: 'Diário alimentar (resumo)', type: 'text' },
    { key: 'comportamento_alimentar', label: 'Comportamento alimentar', type: 'text' },
    { key: 'comportamento_outras', label: 'Outras observações', type: 'text' },
  ]},
  { id: 'observacoes', title: 'Observações gerais', fields: [
    { key: 'observacoes_gerais', label: 'Observações livres', type: 'text' },
  ]},
];
const ANAMNESE_FIELD_LABELS = Object.fromEntries(ANAMNESE_BLOCKS.flatMap(b => b.fields).map(f => [f.key, f.label]));

function AnamneseTab({ patientId }) {
  const confirmClear = useConfirmClear();
  const confirmDelete = useConfirmDelete();
  const [data, setData] = useState({});
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [activeBlock, setActiveBlock] = useState('motivo');
  const [patient, setPatient] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const fetchData = useCallback(async () => {
    try { const { data: d } = await axios.get(`${API}/api/patients/${patientId}/anamnesis`); setData(d); setForm(d); } catch {}
  }, [patientId]);
  const fetchPatient = useCallback(async () => {
    try { const { data: p } = await axios.get(`${API}/api/patients/${patientId}`); setPatient(p); } catch {}
  }, [patientId]);
  useEffect(() => { fetchData(); fetchPatient(); }, [fetchData, fetchPatient]);

  // PROMPT 3 — Item 1: cálculos clínicos (gravidez / pós-parto)
  const patientTypes = useMemo(() => {
    const t = patient?.patient_type ? [patient.patient_type] : [];
    if (Array.isArray(patient?.patient_types)) t.push(...patient.patient_types);
    return t;
  }, [patient]);
  const isGravida = patientTypes.includes('gravida');
  const isPosParto = patientTypes.includes('pos_parto');
  const showClinicalBlock = isGravida || isPosParto;

  const parseNum = (v) => {
    if (v === undefined || v === null || v === '') return null;
    const n = parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const calc = useMemo(() => {
    const altura = parseNum(form.altura) || parseNum(patient?.altura);
    const peso_pre = parseNum(form.peso_pre_gestacional);
    const peso_atual = parseNum(form.peso_atual);
    const peso_parto = parseNum(form.peso_parto);
    const alturaM = altura ? altura / 100 : null;
    const out = {};
    if (isGravida) {
      if (peso_pre && alturaM) {
        const bmi = +(peso_pre / (alturaM * alturaM)).toFixed(1);
        out.imc_pre_gestacional = bmi;
        if (bmi < 18.5) out.ganho_peso_recomendado = '12.5 a 18 kg (Baixo peso)';
        else if (bmi < 25) out.ganho_peso_recomendado = '11.5 a 16 kg (Peso normal)';
        else if (bmi < 30) out.ganho_peso_recomendado = '7 a 11.5 kg (Excesso de peso)';
        else out.ganho_peso_recomendado = '5 a 9 kg (Obesidade)';
      }
      if (peso_atual && peso_pre) out.ganho_peso_atual = +(peso_atual - peso_pre).toFixed(1);
    }
    if (isPosParto) {
      if (peso_atual && alturaM) out.imc_atual = +(peso_atual / (alturaM * alturaM)).toFixed(1);
      if (peso_parto && peso_pre) out.ganho_peso_total = +(peso_parto - peso_pre).toFixed(1);
      if (peso_atual && peso_pre) out.percentagem_peso_retido = +(((peso_atual - peso_pre) / peso_pre) * 100).toFixed(1);
    }
    return out;
  }, [form, patient, isGravida, isPosParto]);

  const save = async () => {
    const textFields = ANAMNESE_BLOCKS.flatMap(b => b.fields.filter(f => f.type === 'text').map(f => ({ key: f.key, label: f.label })));
    const cleared = textFields.filter(f => (data[f.key] || '').toString().trim() && !(form[f.key] || '').toString().trim());
    if (cleared.length > 0) {
      const ok = await confirmClear(cleared.map(f => f.label).join(', '));
      if (!ok) return;
    }
    try {
      await axios.put(`${API}/api/patients/${patientId}/anamnesis`, { ...form, ...calc });
      toast.success('Anamnese guardada'); setEditing(false); fetchData();
    } catch { toast.error('Erro ao guardar'); }
  };

  const restoreAnamnesisVersion = async (snapshot) => {
    const ok = await confirmDelete({
      description: 'Vais repor esta versão anterior da Anamnese. Os campos preenchidos atualmente são substituídos (ainda precisas de clicar em Guardar).',
      confirmLabel: 'Repor esta versão',
    });
    if (!ok) return;
    setForm(f => ({ ...f, ...snapshot }));
    setEditing(true);
    setShowHistory(false);
  };

  const renderField = (f) => {
    const val = form[f.key];
    if (!editing) {
      if (f.type === 'boolean') return <p className="text-sm text-brand-text">{val === true ? 'Sim' : val === false ? 'Não' : '-'}</p>;
      if (f.type === 'scale') return val ? <div className="flex gap-1">{[1,2,3,4,5].map(n => <div key={n} className={`w-7 h-7 rounded-full text-xs flex items-center justify-center font-medium ${n <= val ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted'}`}>{n}</div>)}</div> : <p className="text-sm text-brand-muted">-</p>;
      if (f.type === 'choice') return <p className="text-sm text-brand-text">{Array.isArray(val) ? val.join(', ') : (val || '-')}</p>;
      if (f.type === 'time') return <p className="text-sm text-brand-text">{val || '-'}</p>;
      return <p className="text-sm text-brand-text whitespace-pre-wrap">{val || '-'}</p>;
    }
    if (f.type === 'boolean') return (
      <div className="flex gap-2">{['Sim', 'Não'].map(o => <button key={o} type="button" onClick={() => setForm({...form, [f.key]: o === 'Sim'})} className={`px-4 py-1.5 rounded-full text-xs font-medium ${(val === true && o === 'Sim') || (val === false && o === 'Não') ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{o}</button>)}</div>
    );
    if (f.type === 'scale') return (
      <div className="flex gap-1.5">{[1,2,3,4,5].map(n => <button key={n} type="button" onClick={() => setForm({...form, [f.key]: n})} className={`w-9 h-9 rounded-full text-sm font-medium ${val === n ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{n}</button>)}</div>
    );
    if (f.type === 'choice' && f.options) return (
      <div className="flex flex-wrap gap-1.5">{f.options.map(o => <button key={o} type="button" onClick={() => setForm({...form, [f.key]: o})} className={`px-3 py-1.5 rounded-full text-xs ${val === o ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{o}</button>)}</div>
    );
    if (f.type === 'time') return <Input type="time" value={val || ''} onChange={e => setForm({...form, [f.key]: e.target.value})} className="w-32 rounded-xl border-brand-border text-sm" />;
    return <Textarea value={val || ''} onChange={e => setForm({...form, [f.key]: e.target.value})} className="rounded-xl border-brand-border text-sm" rows={2} data-testid={`anamnese-${f.key}`} />;
  };

  const currentBlock = ANAMNESE_BLOCKS.find(b => b.id === activeBlock) || ANAMNESE_BLOCKS[0];
  const filledCount = (block) => block.fields.filter(f => { const v = data[f.key]; return v !== undefined && v !== null && v !== ''; }).length;

  return (
    <div data-testid="tab-anamnese">
      {/* Action bar */}
      <div className="flex justify-end items-center gap-3 mb-4">
        {(data.anamnesis_history || []).length > 0 && (
          <button onClick={() => setShowHistory(true)} className="text-sm text-brand-muted hover:underline" data-testid="history-btn-anamnese">Ver versões anteriores</button>
        )}
        {editing ? (
          <div className="flex gap-2">
            <button onClick={() => { setEditing(false); setForm(data); }} className="text-sm text-brand-muted">Cancelar</button>
            <button onClick={save} className="bg-brand-green text-white rounded-full px-4 py-1.5 text-sm font-medium" data-testid="save-anamnesis-btn">Guardar</button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} className="text-sm text-brand-green flex items-center gap-1" data-testid="edit-anamnesis-btn"><PencilSimple size={14} /> Editar</button>
        )}
      </div>

      {/* Block navigation */}
      <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1">
        {ANAMNESE_BLOCKS.map(b => {
          const filled = filledCount(b);
          const total = b.fields.length;
          return (
            <button key={b.id} onClick={() => setActiveBlock(b.id)} className={`px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${activeBlock === b.id ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`} data-testid={`block-${b.id}`}>
              {b.title}
              {filled > 0 && <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${activeBlock === b.id ? 'bg-white/20' : 'bg-brand-mint/20 text-brand-green'}`}>{filled}/{total}</span>}
            </button>
          );
        })}
      </div>

      {/* Block content */}
      <div className="space-y-3">
        {currentBlock.fields.map(f => (
          <div key={f.key} className="bg-white border border-brand-border rounded-2xl p-4">
            <Label className="text-xs text-brand-muted mb-2 block">{f.label}</Label>
            {renderField(f)}
          </div>
        ))}
      </div>

      {/* Last updated */}
      {data.updated_at && (
        <p className="text-[10px] text-brand-placeholder mt-4 text-right">Última atualização: {fmtDatePt(data.updated_at?.slice(0, 10))}</p>
      )}
      {/* PROMPT 3 — Item 1: Bloco de dados clínicos (gravidez / pós-parto) */}
      {showClinicalBlock && (
        <div className="mt-5 bg-white border border-brand-border rounded-2xl p-4" data-testid="anamnese-clinical-block">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-medium text-sm text-brand-text">
              Dados clínicos {isGravida ? '(gravidez)' : '(pós-parto)'}
            </h3>
            <span className="text-[10px] uppercase tracking-wider text-brand-green bg-brand-mint/20 px-2 py-0.5 rounded-full">Auto-calculado</span>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <Label className="text-[10px] text-brand-muted mb-1 block">Peso pré-gestacional (kg)</Label>
              {editing ? (
                <Input type="number" step="0.1" value={form.peso_pre_gestacional || ''} onChange={(e) => setForm({ ...form, peso_pre_gestacional: e.target.value })} className="rounded-xl border-brand-border text-sm" data-testid="anamnese-peso-pre-gest" />
              ) : (<p className="text-sm text-brand-text">{data.peso_pre_gestacional ? `${data.peso_pre_gestacional} kg` : '-'}</p>)}
            </div>
            <div>
              <Label className="text-[10px] text-brand-muted mb-1 block">Altura (cm){patient?.altura && !form.altura && <span className="text-[9px] text-brand-green ml-1">(perfil: {patient.altura})</span>}</Label>
              {editing ? (
                <Input type="number" step="0.1" value={form.altura || ''} onChange={(e) => setForm({ ...form, altura: e.target.value })} placeholder={patient?.altura ? String(patient.altura) : ''} className="rounded-xl border-brand-border text-sm" data-testid="anamnese-altura" />
              ) : (<p className="text-sm text-brand-text">{data.altura ? `${data.altura} cm` : (patient?.altura ? `${patient.altura} cm (perfil)` : '-')}</p>)}
            </div>
            <div>
              <Label className="text-[10px] text-brand-muted mb-1 block">Peso atual (kg)</Label>
              {editing ? (
                <Input type="number" step="0.1" value={form.peso_atual || ''} onChange={(e) => setForm({ ...form, peso_atual: e.target.value })} className="rounded-xl border-brand-border text-sm" data-testid="anamnese-peso-atual" />
              ) : (<p className="text-sm text-brand-text">{data.peso_atual ? `${data.peso_atual} kg` : '-'}</p>)}
            </div>
            {isPosParto && (
              <div>
                <Label className="text-[10px] text-brand-muted mb-1 block">Peso ao parto (kg)</Label>
                {editing ? (
                  <Input type="number" step="0.1" value={form.peso_parto || ''} onChange={(e) => setForm({ ...form, peso_parto: e.target.value })} className="rounded-xl border-brand-border text-sm" data-testid="anamnese-peso-parto" />
                ) : (<p className="text-sm text-brand-text">{data.peso_parto ? `${data.peso_parto} kg` : '-'}</p>)}
              </div>
            )}
          </div>
          {/* Calculated read-only fields */}
          <div className="border-t border-brand-border/40 pt-3">
            <p className="text-[10px] uppercase tracking-wider text-brand-muted mb-2">Calculados automaticamente</p>
            <div className="grid grid-cols-2 gap-3">
              {isGravida && (
                <>
                  <div className="bg-brand-bg/40 rounded-xl p-3" data-testid="calc-imc-pre">
                    <Label className="text-[10px] text-brand-muted">IMC pré-gestacional</Label>
                    <p className="text-sm font-medium text-brand-green mt-0.5">{calc.imc_pre_gestacional ?? data.imc_pre_gestacional ?? '-'}{(calc.imc_pre_gestacional || data.imc_pre_gestacional) ? ' kg/m²' : ''}</p>
                  </div>
                  <div className="bg-brand-bg/40 rounded-xl p-3" data-testid="calc-ganho-recomendado">
                    <Label className="text-[10px] text-brand-muted">Ganho recomendado (IOM)</Label>
                    <p className="text-sm font-medium text-brand-green mt-0.5">{calc.ganho_peso_recomendado || data.ganho_peso_recomendado || '-'}</p>
                  </div>
                  <div className="bg-brand-bg/40 rounded-xl p-3" data-testid="calc-ganho-atual">
                    <Label className="text-[10px] text-brand-muted">Ganho de peso atual</Label>
                    <p className="text-sm font-medium text-brand-green mt-0.5">{(calc.ganho_peso_atual ?? data.ganho_peso_atual) != null ? `${calc.ganho_peso_atual ?? data.ganho_peso_atual} kg` : '-'}</p>
                  </div>
                </>
              )}
              {isPosParto && (
                <>
                  <div className="bg-brand-bg/40 rounded-xl p-3" data-testid="calc-imc-atual">
                    <Label className="text-[10px] text-brand-muted">IMC atual</Label>
                    <p className="text-sm font-medium text-brand-green mt-0.5">{(calc.imc_atual ?? data.imc_atual) ?? '-'}{(calc.imc_atual || data.imc_atual) ? ' kg/m²' : ''}</p>
                  </div>
                  <div className="bg-brand-bg/40 rounded-xl p-3" data-testid="calc-ganho-total">
                    <Label className="text-[10px] text-brand-muted">Ganho total na gravidez</Label>
                    <p className="text-sm font-medium text-brand-green mt-0.5">{(calc.ganho_peso_total ?? data.ganho_peso_total) != null ? `${calc.ganho_peso_total ?? data.ganho_peso_total} kg` : '-'}</p>
                  </div>
                  <div className="bg-brand-bg/40 rounded-xl p-3" data-testid="calc-peso-retido">
                    <Label className="text-[10px] text-brand-muted">% peso retido</Label>
                    <p className="text-sm font-medium text-brand-green mt-0.5">{(calc.percentagem_peso_retido ?? data.percentagem_peso_retido) != null ? `${calc.percentagem_peso_retido ?? data.percentagem_peso_retido} %` : '-'}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Pre-consultation responses */}
      <PreConsultResponses patientId={patientId} showPdfExport />
      {/* Habitual intake */}
      <HabitualIntakeSection patientId={patientId} />
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="rounded-3xl border-brand-border max-w-lg" data-testid="anamnese-history-modal">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Versões anteriores da Anamnese</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[28rem] overflow-y-auto">
            {[...(data.anamnesis_history || [])].reverse().map((entry, i) => (
              <div key={i} className="bg-brand-bg/50 rounded-xl p-3" data-testid={`anamnese-history-entry-${i}`}>
                <p className="text-xs text-brand-muted mb-2">{entry.replaced_by_name ? `${entry.replaced_by_name} · ` : ''}{entry.replaced_at ? fmtStamp(entry.replaced_at) : ''}</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {Object.entries(entry.snapshot || {}).filter(([k, v]) => ANAMNESE_FIELD_LABELS[k] && v !== '' && v != null).map(([k, v]) => (
                    <p key={k} className="text-xs text-brand-text"><span className="text-brand-muted">{ANAMNESE_FIELD_LABELS[k]}:</span> {Array.isArray(v) ? v.join(', ') : String(v)}</p>
                  ))}
                </div>
                <button type="button" onClick={() => restoreAnamnesisVersion(entry.snapshot)} className="mt-2 text-xs text-brand-green font-medium hover:underline" data-testid={`anamnese-restore-btn-${i}`}>Repor esta versão</button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

}

// ─── HABITUAL INTAKE ───
function HabitualIntakeSection({ patientId }) {
  const [data, setData] = useState({ meals: [] });
  const [editing, setEditing] = useState(false);
  const MEAL_SLOTS = ['pequeno_almoco','meio_manha','almoco','lanche','jantar','ceia'];
  const MEAL_LABELS = { pequeno_almoco: 'Pequeno-almoço', meio_manha: 'Meio da manhã', almoco: 'Almoço', lanche: 'Lanche', jantar: 'Jantar', ceia: 'Ceia' };
  const fetch = useCallback(async () => { try { const { data: d } = await axios.get(`${API}/api/patients/${patientId}/habitual-intake`); setData(d || { meals: [] }); } catch {} }, [patientId]);
  useEffect(() => { fetch(); }, [fetch]);
  const getMeal = (slot) => (data.meals || []).find(m => m.slot === slot) || { slot, description: '', time: '', frequency: '', notes: '' };
  const updateMeal = (slot, field, value) => {
    const meals = [...(data.meals || [])];
    const idx = meals.findIndex(m => m.slot === slot);
    if (idx >= 0) { meals[idx] = { ...meals[idx], [field]: value }; }
    else { meals.push({ slot, [field]: value }); }
    setData({ ...data, meals });
  };
  const save = async () => { try { await axios.put(`${API}/api/patients/${patientId}/habitual-intake`, { meals: data.meals }); toast.success('Consumo habitual guardado'); setEditing(false); } catch { toast.error('Erro'); } };
  return (
    <div className="mt-4" data-testid="habitual-intake">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] text-brand-muted uppercase tracking-wider">Consumo alimentar habitual</p>
        <button onClick={() => { if (editing) save(); else setEditing(true); }} className="text-[10px] text-brand-green">{editing ? 'Guardar' : 'Editar'}</button>
      </div>
      <div className="space-y-2">
        {MEAL_SLOTS.map(slot => {
          const m = getMeal(slot);
          return (
            <div key={slot} className="bg-white border border-brand-border rounded-xl p-2.5">
              <p className="text-[10px] font-medium text-brand-green mb-1">{MEAL_LABELS[slot]}</p>
              {editing ? (
                <div className="space-y-1">
                  <Input value={m.description || ''} onChange={e => updateMeal(slot, 'description', e.target.value)} placeholder="O que come habitualmente..." className="rounded-lg border-brand-border text-xs h-7" />
                  <div className="grid grid-cols-2 gap-1">
                    <Input value={m.time || ''} onChange={e => updateMeal(slot, 'time', e.target.value)} placeholder="Hora" className="rounded-lg border-brand-border text-[10px] h-6" />
                    <select value={m.frequency || ''} onChange={e => updateMeal(slot, 'frequency', e.target.value)} className="border border-brand-border rounded-lg px-1 text-[10px] h-6 bg-white">
                      <option value="">Frequência</option><option value="diaria">Diária</option><option value="3-4x">3-4x semana</option><option value="fins_semana">Fins de semana</option><option value="ocasional">Ocasional</option>
                    </select>
                  </div>
                </div>
              ) : (
                m.description ? <Md as="p" className="text-xs text-brand-text">{`${m.description}${m.time ? ` (${m.time})` : ''}${m.frequency ? ` [${m.frequency}]` : ''}`}</Md> : <p className="text-[9px] text-brand-placeholder italic">Sem registo</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AnamneseTab;
